import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { formatCurrency } from 'utils';
import LabelWrapper from '../LabelWrapper';
import { StyledInputNumber, StyledAddonAfter } from './StyledComponents';
import { InputNumberProps } from './types';

// Match any character that isn't a digit or decimal place
// eslint-disable-next-line no-useless-escape
const specialCharsRe = /[^\d\.]/g;
// Match valid decimal numbers (0+ digits, optionally followed by a decimal
// place and 0+ more digits, but with at least 1 digit somewhere)
// eslint-disable-next-line no-useless-escape
const decimalNumberRe = /^(\d+(\.\d*)?|\.\d+)$/;

export const InputNumber: React.FC<InputNumberProps> = ({
  value,
  type,
  label,
  placeholder,
  error,
  required,
  width,
  hideArrows,
  alwaysShowArrows,
  size = 'middle',
  fractionDigits,
  addonAfter,
  className,
  ...props
}) => {
  // This is only meant to update the display value on blur—we can't use this in
  // the built-in formatter prop as `formatCurrency` interferes with the ability
  // to enter decimal values
  const formatValue = (input: React.ReactText) => {
    const str = input?.toString() || '';

    if (type === 'currency') {
      if (!decimalNumberRe.test(str)) return '';
      try {
        return formatCurrency(str);
      } catch (err) {
        return str;
      }
    }

    return str;
  };

  // This runs as you type and is sent in the change event
  const parseValue = (input: string) => {
    if (!input) return input;

    const noSpecialChars = input.replace(specialCharsRe, '');
    const [characteristic, ...mantissaGroups] = noSpecialChars.split('.');
    const noDuplicateDecimals = mantissaGroups.length
      ? `${characteristic}.${mantissaGroups.join('')}`
      : characteristic;

    if (!noDuplicateDecimals) return undefined;
    return noDuplicateDecimals;
  };

  // The display value will only be non-null when the input is blurred, and its
  // value will be calculated on blur.
  const [displayValue, setDisplayValue] = useState<string | null>(
    formatValue(value),
  );

  useEffect(() => setDisplayValue(formatValue(value)), [value]);

  return (
    <LabelWrapper
      label={label}
      error={error}
      required={required}
      className={className}
    >
      <StyledInputNumber
        $width={width}
        $hideArrows={hideArrows}
        $alwaysShowArrows={alwaysShowArrows}
        $error={error}
        placeholder={placeholder || (typeof label === 'string' ? label : null)}
        onFocus={() => setDisplayValue(null)}
        onBlur={() => setDisplayValue(formatValue(value))}
        parser={parseValue}
        // @ts-ignore We sepcifically want to, and the component is able to, show formatted strings
        value={displayValue || value}
        size={size}
        {...props}
      />
      {addonAfter && (
        <StyledAddonAfter $size={size}>{addonAfter}</StyledAddonAfter>
      )}
    </LabelWrapper>
  );
};

export default styled(InputNumber)``;
